/**
 * The left pane of the configure screen: what the evaluator is, in three
 * numbered steps. 1 its name; 2 the prompt, or the check and its options, and
 * the pass score; 3 where each variable reads from.
 */

import { useState } from "react";
import { Box } from "@/components/ui/box";
import { Field } from "@/components/ui/field";
import { CheckOptions } from "./check-options";
import { CheckPicker } from "./check-picker";
import { PromptEditor } from "./prompt-editor";
import { Step, Steps } from "./setup/stepper";
import { missingIn, VariableRows } from "./setup/variable-rows";
import type { CodeCheck, PathInfo } from "./types";
import type { useEvaluatorDraft } from "./use-evaluator-draft";

type DraftApi = ReturnType<typeof useEvaluatorDraft>;

const plural = (n: number, one: string) => `${n} ${one}${n === 1 ? "" : "s"}`;

function Threshold({ d }: { d: DraftApi }) {
  return (
    <Field label="Pass when score ≥" htmlFor="ev-threshold" hint="Every score is between 0 and 1.">
      <input
        id="ev-threshold"
        className="inp w-28 font-mono"
        type="number"
        min={0}
        max={1}
        step={0.05}
        value={d.draft.threshold}
        onChange={(e) => d.setThreshold(e.target.value)}
      />
    </Field>
  );
}

export function DefinitionPane({
  d,
  checks,
  model,
  paths,
  sample,
  focused,
  onFocus,
}: {
  d: DraftApi;
  checks: CodeCheck[];
  model: string | null;
  paths: PathInfo[];
  sample?: Record<string, unknown> | undefined;
  focused: string | null;
  onFocus: (name: string) => void;
}) {
  const [folded, setFolded] = useState<Record<number, boolean>>({});
  const fold = (n: number) => () => setFolded((f) => ({ ...f, [n]: !f[n] }));
  const judge = d.draft.kind === "llm_judge";
  const missing = missingIn(d.rows, d.mapping, sample);
  const unmapped = d.rows.filter((r) => r.required && !d.mapping[r.name]?.trim()).length;
  const defined = judge ? !!d.draft.prompt.trim() : !!d.check;
  const scoring = `Pass from ${d.draft.threshold}`;

  return (
    <Box className="p-5 max-sm:px-3.5" label="Definition">
      <Steps label="Set up the evaluator">
        <Step
          n={1}
          title="Name evaluator"
          hint="How it shows in the evaluator list and on experiment results."
          done={!!d.draft.name.trim()}
          summary={d.draft.name.trim() || "No name yet"}
          open={!folded[1]}
          onToggle={fold(1)}
        >
          <Field label="Name" htmlFor="ev-name">
            <input
              id="ev-name"
              className="inp"
              value={d.draft.name}
              placeholder={judge ? "e.g. Answers the question" : "e.g. Exact answer"}
              onChange={(e) => d.setName(e.target.value)}
            />
          </Field>
        </Step>
        <Step
          n={2}
          title="Define evaluation"
          hint={judge ? "Say what the judge checks and when it passes." : "Pick the check and its options."}
          done={defined}
          summary={
            judge
              ? `${d.draft.prompt.trim() ? "Prompt written" : "No prompt yet"} · ${scoring}`
              : `${d.check?.name ?? "No check yet"} · ${scoring}`
          }
          open={!folded[2]}
          onToggle={fold(2)}
        >
          <div className="flex flex-col gap-4">
            {judge && (
              <div className="flex items-center gap-2 text-[13px]">
                <span className="font-semibold">Model</span>
                <span className="pill" aria-label="Model">
                  {model ? `${model} · workspace default` : "Workspace default"}
                </span>
              </div>
            )}
            {judge ? (
              <PromptEditor id="ev-prompt" value={d.draft.prompt} onChange={d.setPrompt} />
            ) : (
              <CheckPicker checks={checks} value={d.draft.check} onChange={d.setCheck} />
            )}
            {d.check && (
              <CheckOptions specs={d.check.options} value={d.draft.options} onChange={d.setOption} />
            )}
            <Threshold d={d} />
          </div>
        </Step>
        <Step
          n={3}
          title={judge ? "Map variables to data" : "Compare"}
          hint={
            judge
              ? "Each {{name}} in the prompt reads one field of the sample. Open a row to see its value."
              : "Which fields of the sample the check compares."
          }
          done={d.rows.length > 0 && unmapped === 0 && missing.length === 0}
          warn={missing.length > 0}
          summary={
            d.rows.length === 0
              ? "No variables yet"
              : unmapped
                ? `${plural(unmapped, "variable")} not mapped`
                : missing.length
                  ? `${plural(missing.length, "field")} not in this sample`
                  : `${plural(d.rows.length, "variable")} mapped`
          }
          open={!folded[3]}
          onToggle={fold(3)}
          last
        >
          {judge || d.check ? (
            <VariableRows
              rows={d.rows}
              value={d.mapping}
              paths={paths}
              sample={sample}
              focused={focused}
              onFocus={onFocus}
              onChange={d.setPath}
            />
          ) : (
            <p className="m-0 text-xs text-muted">Pick a check first.</p>
          )}
        </Step>
      </Steps>
    </Box>
  );
}
